import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { PeriodTypes } from '../../../lib/types/models/period/period'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledColorInput from '../../elements/inputs/controlled-color-input'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<PeriodTypes.Input> & {
  className?: string
}

const formSchema: ZodType<PeriodTypes.Input> = z
  .object({
    name: createStringSchema('NOM (FR)'),
    nameEn: createStringSchema('NOM (EN)'),
    startDate: createDateStringSchema({ inputName: 'date de début' }),
    endDate: createDateStringSchema({ inputName: 'date de fin' }),
    color: createStringSchema('couleur').default('#000000'),
    displayName: createStringSchema('affichage site', { nullish: true })
  })
  .superRefine((arg, ctx) => {
    // check if the start date is before the end date
    if (arg.startDate && arg.endDate && arg.startDate > arg.endDate) {
      ctx.addIssue({
        path: ['startDate'],
        code: z.ZodIssueCode.custom,
        message: 'La date de début ne peut pas être après la date de fin'
      })
    }
  })

const PeriodForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<PeriodTypes.Input>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { startDate, endDate } = form.watch()

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      form.setError('startDate', {
        message: 'La date de début ne peut pas être après la date de fin'
      })
    }
  }, [startDate, endDate])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput required type="text" label="NOM (FR)" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nameEn"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput required type="text" label="NOM (EN)" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    label="Date de début"
                    mode="over"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput {...field} required label="Date de fin" mode="over" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput type="text" label="Affichage site" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledColorInput
                    label="Couleur"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default PeriodForm
